import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, CheckCircle2 } from "lucide-react";

interface SheetRow {
  id: string;
  original_filename: string;
  uploaded_at: string;
  is_active: boolean;
  notes: string | null;
  storage_path: string;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // strip data: prefix
      resolve(result.split(",")[1] || "");
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function RecommendationEngineCard() {
  const { isSuperadmin } = useIsSuperadmin();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { data: sheets = [] } = useQuery({
    queryKey: ["harvest_recommendation_sheets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("harvest_recommendation_sheets")
        .select("id, original_filename, uploaded_at, is_active, notes, storage_path")
        .order("uploaded_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as SheetRow[];
    },
  });

  const activate = useMutation({
    mutationFn: async (id: string) => {
      const { error: e1 } = await supabase
        .from("harvest_recommendation_sheets")
        .update({ is_active: false })
        .eq("is_active", true);
      if (e1) throw e1;
      const { error: e2 } = await supabase
        .from("harvest_recommendation_sheets")
        .update({ is_active: true })
        .eq("id", id);
      if (e2) throw e2;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["harvest_recommendation_sheets"] });
      toast.success("Sheet activated");
    },
    onError: (e: any) => toast.error(`Activate failed: ${e.message}`),
  });

  const handleFile = async (file: File | null) => {
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) {
      toast.error("Please choose an .xlsx file");
      return;
    }
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const { data, error } = await supabase.functions.invoke("upload-harvest-recommendations", {
        body: { filename: file.name, base64, notes: notes || null },
      });
      if (error) throw error;
      toast.success("Recommendation sheet uploaded and activated");
      setNotes("");
      queryClient.invalidateQueries({ queryKey: ["harvest_recommendation_sheets"] });
      void data;
    } catch (e: any) {
      toast.error(`Upload failed: ${e.message ?? e}`);
    } finally {
      setUploading(false);
    }
  };

  const downloadName = (row: SheetRow) => {
    const dupes = sheets.filter((s) => s.original_filename === row.original_filename).length > 1;
    if (!dupes) return row.original_filename;
    const d = new Date(row.uploaded_at);
    const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return row.original_filename.replace(/(\.xlsx)$/i, `_${stamp}$1`);
  };

  const triggerAnchor = (href: string, name: string, newTab: boolean) => {
    const a = document.createElement("a");
    a.href = href;
    a.download = name;
    if (newTab) {
      a.target = "_blank";
      a.rel = "noopener";
    }
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDownload = async (row: SheetRow) => {
    const name = downloadName(row);
    setDownloadingId(row.id);
    try {
      const { data: blob, error } = await supabase.storage
        .from("harvest-recommendation-sheets")
        .download(row.storage_path);
      if (error || !blob) throw error ?? new Error("Empty file");
      const url = URL.createObjectURL(blob);
      triggerAnchor(url, name, false);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (primaryErr: any) {
      const { data, error } = await supabase.storage
        .from("harvest-recommendation-sheets")
        .createSignedUrl(row.storage_path, 60, { download: name });
      if (error || !data?.signedUrl) {
        toast.error(`Download failed: ${error?.message ?? primaryErr?.message ?? "unknown error"}`);
        return;
      }
      triggerAnchor(data.signedUrl, name, true);
    } finally {
      setDownloadingId(null);
    }
  };


  if (!isSuperadmin) {
    return (
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex items-center gap-2 mb-1">
          <FileSpreadsheet className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-foreground">Recommendation Engine</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Only superadmins can upload or change the recommendation sheet.
        </p>
        {sheets.find((s) => s.is_active) && (
          <p className="text-xs text-muted-foreground mt-2">
            Active sheet:{" "}
            <span className="font-medium text-foreground">
              {sheets.find((s) => s.is_active)?.original_filename}
            </span>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-xl border border-border p-4 space-y-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-foreground">Recommendation Engine</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Upload an Excel workbook containing the <code>Inputs</code> and <code>Recommendations</code> tabs.
          The new file is activated immediately; the cache is cleared.
        </p>
        <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-foreground mb-1 block">Notes (optional)</label>
            <Textarea
              rows={2}
              placeholder="e.g. v3 — added heat-pump dryer rules"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <label className="cursor-pointer">
            <Input
              type="file"
              accept=".xlsx"
              className="hidden"
              disabled={uploading}
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            />
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity">
              <Upload className="w-4 h-4" />
              {uploading ? "Uploading..." : "Upload .xlsx"}
            </span>
          </label>
        </div>
      </div>

      {sheets.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-4 space-y-2">
          <h4 className="text-sm font-semibold text-foreground mb-2">Versions</h4>
          {sheets.map((row) => (
            <div
              key={row.id}
              className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground truncate">{row.original_filename}</span>
                  {row.is_active && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary/10 text-primary rounded">
                      <CheckCircle2 className="w-3 h-3" /> Active
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Uploaded {new Date(row.uploaded_at).toLocaleString()}
                  {row.notes && ` · ${row.notes}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownload(row)}
                  disabled={downloadingId === row.id}
                  className="gap-1"
                >
                  <Download className="w-3.5 h-3.5" />
                  {downloadingId === row.id ? "Downloading..." : "Download"}
                </Button>
                {!row.is_active && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => activate.mutate(row.id)}
                    disabled={activate.isPending}
                  >
                    Make active
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
